import { useEffect, useState } from 'react'
import axios from 'axios'
import { getPcekZone } from '@entities/user'
import { MESSAGE, reqHeaders } from '@shared/config'
import { Payee } from '@root/api/ump-catalog-manager'
import { baseUmpPath } from '@root/api/api'
import { DenominationsPricesByCountryAndServiceIdType } from './denomination.types'

export const useGetPrices = (denominationsByCountry: Record<string, Payee[]>) => {
  const [pricesByCountry, setPricesByCountry] = useState<DenominationsPricesByCountryAndServiceIdType | null>(null)
  const [isPricesLoading, setIsPricesLoading] = useState(false)
  const [pricesError, setPricesError] = useState<string>('')

  const zone = getPcekZone()

  const fetchPrice = async (payee: Payee) => {
    if (process.env.NODE_ENV === 'development') {
      return new Promise((resolve) => {
        setTimeout(() => resolve('345'), 1000)
      })
    }

    const denominationParams =
      payee.params?.reduce((acc, param) => ({ ...acc, [param.name]: { value: param.defaultValue } }), {
        partnerId: { value: '' },
        serviceCode: { value: '' },
      }) || null

    try {
      const response = await axios.get(
        `${baseUmpPath}/${zone}/games/getPrice?serviceCode=${denominationParams?.serviceCode.value}&partner=${denominationParams?.partnerId.value}`,
        { headers: { ...reqHeaders } }
      )

      return response.data.price
    } catch (error) {
      return ''
    }
  }

  const fetchAllPrices = async () => {
    const countries = Object.keys(denominationsByCountry)

    if (countries.length === 0) {
      setPricesByCountry(null)
      setIsPricesLoading(false)

      return
    }

    try {
      setIsPricesLoading(true)
      setPricesError('')

      const countryPromises = countries.map(async (country) => {
        const payees = denominationsByCountry[country]

        const servicePromises = payees.map(async (payee) => {
          try {
            const price = await fetchPrice(payee)

            return { serviceId: payee.serviceId, price }
          } catch (err) {
            // onError?.(err as Error, country, payee.serviceId)
            return { serviceId: payee.serviceId, price: null }
          }
        })

        const serviceResults = await Promise.all(servicePromises)

        const servicesMap = serviceResults.reduce<Record<string, string | null>>((acc, { serviceId, price }) => {
          acc[serviceId] = price

          return acc
        }, {})

        return { country, pricesByServiceId: servicesMap }
      })

      const result = await Promise.all(countryPromises)

      const pricesMap = result.reduce<DenominationsPricesByCountryAndServiceIdType>(
        (acc, { country, pricesByServiceId }) => {
          acc[country] = Object.entries(pricesByServiceId).reduce<Record<string, string>>(
            (countryAcc, [serviceId, price]) => {
              if (price !== null) {
                countryAcc[serviceId] = price
              }

              return countryAcc
            },
            {}
          )

          return acc
        },
        {}
      )

      setPricesByCountry(pricesMap)
    } catch (err: unknown) {
      setPricesError((err as Error)?.message || MESSAGE)
      setPricesByCountry(null)
    } finally {
      setIsPricesLoading(false)
    }
  }

  useEffect(() => {
    fetchAllPrices()
  }, [])

  return { pricesByCountry, isPricesLoading, pricesError }
}
